import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Mail, CheckCircle, XCircle, Loader2, AlertTriangle } from 'lucide-react';
import Button from '../components/Button';
import api from '../lib/api';

const CalendarUnsubscribePage: React.FC = () => {
  // Get token from URL path parameter (e.g., /calendar-unsubscribe/:token)
  const { token } = useParams<{ token: string }>();
  
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'confirm'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('Missing unsubscribe token. Please use the link from your email.');
      return;
    }
    
    // Show confirmation screen instead of auto-unsubscribing
    setStatus('confirm');
  }, [token]);

  const handleUnsubscribe = async () => {
    if (!token) return;
    
    setSubmitting(true);
    try {
      await api.post('/calendar-emails/unsubscribe', { token, reason });
      setStatus('success');
    } catch (error: any) {
      setStatus('error');
      setErrorMessage(error.response?.data?.error || 'Failed to unsubscribe. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-background-dark-primary flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="card text-center">
          {/* Header */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
              <Mail className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            </div>
          </div>

          <h1 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary mb-2">
            Calendar Email Digest
          </h1>

          {/* Loading State */}
          {status === 'loading' && (
            <div className="py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto" />
              <p className="mt-4 text-text-secondary dark:text-text-dark-secondary">
                Processing...
              </p>
            </div>
          )}

          {/* Confirmation State */}
          {status === 'confirm' && (
            <div className="py-4">
              <p className="text-text-secondary dark:text-text-dark-secondary mb-6">
                Are you sure you want to unsubscribe from the calendar digest emails?
              </p>

              <div className="mb-6 text-left">
                <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-2">
                  Reason for unsubscribing (optional)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Let us know why you're unsubscribing..."
                  className="w-full p-3 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary resize-none"
                  rows={3}
                />
              </div>

              <div className="flex gap-3 justify-center">
                <Link to="/">
                  <Button variant="outline">
                    Cancel
                  </Button>
                </Link>
                <Button
                  variant="primary"
                  onClick={handleUnsubscribe}
                  disabled={submitting}
                  icon={submitting ? Loader2 : undefined}
                >
                  {submitting ? 'Unsubscribing...' : 'Unsubscribe'}
                </Button>
              </div>
            </div>
          )}

          {/* Success State */}
          {status === 'success' && (
            <div className="py-4">
              <div className="flex justify-center mb-4">
                <CheckCircle className="w-12 h-12 text-green-500" />
              </div>
              <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-2">
                Successfully Unsubscribed
              </h2>
              <p className="text-text-secondary dark:text-text-dark-secondary mb-6">
                You will no longer receive calendar digest emails.
              </p>
              <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary mb-6">
                Changed your mind? You can resubscribe anytime from your account settings.
              </p>
              <Link to="/">
                <Button variant="primary">
                  Go to Homepage
                </Button>
              </Link>
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <div className="py-4">
              <div className="flex justify-center mb-4">
                {errorMessage.includes('expired') ? (
                  <AlertTriangle className="w-12 h-12 text-amber-500" />
                ) : (
                  <XCircle className="w-12 h-12 text-red-500" />
                )}
              </div>
              <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-2">
                Unable to Unsubscribe
              </h2>
              <p className="text-text-secondary dark:text-text-dark-secondary mb-6">
                {errorMessage}
              </p>
              <div className="flex gap-3 justify-center">
                <Link to="/">
                  <Button variant="outline">
                    Go to Homepage
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="primary">
                    Log In to Manage
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-text-tertiary dark:text-text-dark-tertiary mt-4">
          © {new Date().getFullYear()} Capitol Commons
        </p>
      </div>
    </div>
  );
};

export default CalendarUnsubscribePage;
